import type { ShopCategory, ShopProduct, ShopSport } from "../../../api/shop";
import { formatEGP } from "../shopFormat";

export type StockPart = { text: string; tone?: "muted" | "danger" };

/** "12 SKUs · 48 in stock · 3 sold out", or "Always available" when stock isn't tracked. Switched-off SKUs don't count. */
export function stockSummary(product: Pick<ShopProduct, "trackStock" | "variants">): StockPart[] {
  if (!product.trackStock) return [{ text: "Always available", tone: "muted" }];
  const on = product.variants.filter((v) => v.isEnabled);
  const inStock = on.reduce((sum, v) => sum + Math.max(0, v.stock ?? 0), 0);
  const soldOut = on.filter((v) => (v.stock ?? 0) <= 0).length;
  const parts: StockPart[] = [{ text: `${on.length} ${on.length === 1 ? "SKU" : "SKUs"}` }, { text: `${inStock} in stock` }];
  if (soldOut > 0) parts.push({ text: `${soldOut} sold out`, tone: "danger" });
  return parts;
}

/** What customers pay: "From 600.00 EGP" when switched-on SKUs cost different amounts, else the one price. */
export function priceLabel(product: Pick<ShopProduct, "price" | "variants">): string {
  const prices = product.variants.filter((v) => v.isEnabled).map((v) => v.price);
  if (prices.length === 0) return formatEGP(product.price);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min < max ? `From ${formatEGP(min)}` : formatEGP(min);
}

type CategoryFields = Pick<ShopProduct, "categoryId" | "subcategoryId" | "category">;

/** ["Apparel", "T-shirts"], ["Gear"], [the old category text], or null when the product has no category yet. */
export function categoryPathParts(product: CategoryFields, categories: ShopCategory[]): string[] | null {
  const parent = categories.find((c) => c.id === product.categoryId);
  if (!parent) return product.category ? [product.category] : null;
  const sub = product.subcategoryId ? parent.subcategories.find((s) => s.id === product.subcategoryId) : undefined;
  return sub ? [parent.name, sub.name] : [parent.name];
}

/** "Apparel › T-shirts", "Gear", the old category text, or null when the product has no category yet. */
export const categoryPath = (product: CategoryFields, categories: ShopCategory[]): string | null =>
  categoryPathParts(product, categories)?.join(" › ") ?? null;

/** The product's sports in catalog order. None means all sports. */
export function productSports(product: Pick<ShopProduct, "sportIds">, sports: ShopSport[]): ShopSport[] {
  const ids = new Set(product.sportIds);
  return [...sports].sort((a, b) => a.displayOrder - b.displayOrder).filter((s) => ids.has(s.id));
}

/** The list's search: the name, the product code or any SKU, ignoring case. */
export function matchesProductSearch(product: Pick<ShopProduct, "name" | "code" | "variants">, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    product.name.toLowerCase().includes(q) ||
    (product.code || "").toLowerCase().includes(q) ||
    product.variants.some((v) => (v.sku || "").toLowerCase().includes(q))
  );
}

/** The list's thumbnail: the first style's cover. */
export const productCover = (product: Pick<ShopProduct, "imageUrls" | "styles">): string | null =>
  product.imageUrls[0] ?? product.styles.find((s) => s.imageUrls.length > 0)?.imageUrls[0] ?? null;
